import { EnvironmentHttpApi, EnvironmentHttpCommonError } from "@t3tools/contracts";
import { httpHeaderRedactionLayer } from "@t3tools/shared/httpObservability";
import * as Data from "effect/Data";
import * as Duration from "effect/Duration";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { FetchHttpClient, HttpClientError } from "effect/unstable/http";
import * as HttpApiClient from "effect/unstable/httpapi/HttpApiClient";
/**
 * HTTP plumbing for talking to a Dovod server's environment API.
 * `remoteHttpClientLayer` below carries every primary-environment request.
 */
const DEFAULT_ENVIRONMENT_REQUEST_TIMEOUT_MS = 10_000;
const isEnvironmentHttpCommonError = Schema.is(EnvironmentHttpCommonError);
export const environmentEndpointUrl = (httpBaseUrl, pathname) => {
    const url = new URL(httpBaseUrl);
    url.pathname = pathname;
    url.search = "";
    url.hash = "";
    return url.toString();
};
const environmentApiBaseUrl = (httpBaseUrl) => {
    const url = new URL(httpBaseUrl);
    url.pathname = "/";
    url.search = "";
    url.hash = "";
    return url.toString();
};
export class EnvironmentRequestFetchError extends Data.TaggedError("EnvironmentRequestFetchError") {
}
export class EnvironmentRequestInvalidJsonError extends Data.TaggedError("EnvironmentRequestInvalidJsonError") {
}
export class EnvironmentRequestUndeclaredStatusError extends Data.TaggedError("EnvironmentRequestUndeclaredStatusError") {
    constructor(requestUrl, status) {
        super({
            message: `Environment endpoint ${requestUrl} returned undeclared status ${status}.`,
            requestUrl,
            status,
        });
    }
}
export class EnvironmentRequestTimeoutError extends Data.TaggedError("EnvironmentRequestTimeoutError") {
    constructor(requestUrl, timeoutMs) {
        super({
            message: `Environment endpoint ${requestUrl} timed out after ${timeoutMs}ms.`,
            requestUrl,
            timeoutMs,
        });
    }
}
/**
 * A fetch-backed HTTP client with header redaction already merged in, so
 * `authorization` never reaches a trace. Takes the `fetch` implementation rather
 * than closing over the global one, which is what lets a test drive it.
 */
export const environmentHttpClientLayer = (fetchFn) => Layer.merge(FetchHttpClient.layer.pipe(Layer.provide(Layer.succeed(FetchHttpClient.Fetch, fetchFn))), httpHeaderRedactionLayer);
const failEnvironmentRequest = (requestUrl, cause) => {
    if (cause instanceof EnvironmentRequestTimeoutError) {
        return Effect.fail(cause);
    }
    if (isEnvironmentHttpCommonError(cause)) {
        return Effect.fail(cause);
    }
    if (Schema.isSchemaError(cause)) {
        return Effect.fail(new EnvironmentRequestInvalidJsonError({
            message: `Environment endpoint returned an invalid response from ${requestUrl}.`,
            cause,
        }));
    }
    if (HttpClientError.isHttpClientError(cause) && cause.response !== undefined) {
        const response = cause.response;
        if (response.status < 200 || response.status >= 300) {
            return Effect.fail(new EnvironmentRequestUndeclaredStatusError(requestUrl, response.status));
        }
        return Effect.fail(new EnvironmentRequestInvalidJsonError({
            message: `Environment endpoint returned an invalid response from ${requestUrl}.`,
            cause,
        }));
    }
    return Effect.fail(new EnvironmentRequestFetchError({
        message: `Failed to fetch environment endpoint ${requestUrl} (${String(cause)}).`,
        cause,
    }));
};
export const executeEnvironmentRequest = (requestUrl, request, timeoutMs = DEFAULT_ENVIRONMENT_REQUEST_TIMEOUT_MS) => request.pipe(Effect.timeoutOption(Duration.millis(timeoutMs)), Effect.flatMap(Option.match({
    onNone: () => Effect.fail(new EnvironmentRequestTimeoutError(requestUrl, timeoutMs)),
    onSome: Effect.succeed,
})), Effect.catch((cause) => failEnvironmentRequest(requestUrl, cause)));
export const makeEnvironmentHttpApiClient = (httpBaseUrl) => HttpApiClient.make(EnvironmentHttpApi, {
    baseUrl: environmentApiBaseUrl(httpBaseUrl),
});
